<template>
    <div class="list-container">
      <div v-show="!dialogFormVisible">
        <div class="filter-container">
          <div class="filter-box">
            <el-input
                v-model="listQuery.name"
                style="width: 200px"
                :size="t('table.btnSize')"
                class="filter-item"
                clearable
                @keyup.enter="handleFilter"
            />
            <el-button
                v-waves
                style="margin-left: 10px"
                class="filter-item "
                type="primary"
                :size="t('table.btnSize')"
                @click="handleFilter"
            >
                {{ t("table.search") }}
            </el-button>
            <el-button
              v-permission="{ pkey: `${permissionName}_ADD` }"
              class="filter-item"
              type="primary"
              :size="t('table.btnSize')"
              @click="handleCommand('add')"
            >
              {{ t("table.add") }}
            </el-button>
            <el-button
                v-waves
                class="filter-item ml-10"
                :size="t('table.btnSize')"
                @click="handleReset"
            >
                {{ t("table.reset") }}
            </el-button>
            <span class="svg-icon cursor mr-10 ml-10" @click="handleCommand('refresh')">
              <i class="icon-tubiao-27 iconfont"/>
            </span>
          </div>
        </div>
         <TableList
            v-loading="listLoading"
            :dataList="list"
            :total="total"
            :columnAllData="columnAllData"
            :page="listQuery.page"
            :size="listQuery.size"
            :tableName="TableName.TABLE_ROLE"
            :permissionName="permissionName"
            @command-change="handleCommand"
            :canRefresh="false"
            :canChangeColumn="false"
            :highlightCurrentRow="true"
          >
            <template v-slot:operation-action="scope">
              <el-link
                v-permission="{ pkey: `${permissionName}_EDIT` }"
                href="javascript:void(0);"
                class="mr-10"
                type="primary"
                @click="handleCommand('edit', scope.row)"
              >
                {{ t("table.edit") }}
              </el-link>
              <el-link
                v-permission="{ pkey: `${permissionName}_DELETE` }"
                href="javascript:void(0);"
                type="danger"
                @click="handleCommand('delete', scope.row)"
              >
                {{ t("table.delete") }}
              </el-link>
            </template>
          </TableList>
      </div>
      <FormComponent
        v-if="dialogFormVisible"
        :id="activeId"
        :initFormData="initFormData"
        :fromFieldData="fromFieldData"
        :dialogFormVisible="dialogFormVisible"
        :handleResolveTempData="handleResolveTempData"
        :api="'role'"
        @update:visibel="() => dialogFormVisible = false"
        @submit="handleSubmit(formData)"
      />
    </div>
</template>

<script lang='ts'>
import { defineComponent, reactive, toRefs, ref, onMounted } from 'vue'
import { ElForm, ElMessage, ElMessageBox } from 'element-plus'
import { cloneDeep } from 'lodash'

import { getRoleListByRoleId, deleteRolesById, getRoleList } from '@/apis/roles'
import { getMenuTree } from '@/apis/menu'

import { RolesModel, MenuModel } from '@/model/Index'
import { useI18n } from 'vue-i18n'

import { getDropdownByType, DropdownOptionModel } from '@/apis/tool'
import FormComponent from '@/components/Form/index.vue'
import TableList from '@/components/common/TableList.vue'
import { TableName, permissionName } from '@/utils/enum'
import { FromFieldData } from './FieldFrom'
import { TableField } from './FieldTable'

export default defineComponent({
  name: 'Role',
  components: {
    FormComponent,
    TableList
  },
  setup() {
    const { t } = useI18n()
    const dataForm = ref(ElForm)
    const dataMap = reactive({
      TableName,
      permissionName: permissionName.ROLE,
      initFormData: cloneDeep(FromFieldData),
      fromFieldData: FromFieldData,
      columnAllData: TableField,

      activeId: 0,
      list: [],
      dataAuthorityOption: [],
      menuList: [],
      total: 0,
      listLoading: true,
      listQuery: {
        name: undefined,
        page: 1,
        size: 10
      },
      dialogFormVisible: false,
      dialogStatus: '',
      submitLoading: false,
      menuTree: [],
      textMap: {
        update: '编辑',
        create: '新建'
      },
      rules: {
        name: [{ required: true, message: '请输入部门名称', trigger: 'change' }],
        dataAuthority: [{ required: true, message: '请选择数据权限', trigger: 'change' }],
        accessIds: [{ required: true, message: '请输入', trigger: 'change' }]
      },

      getDropdownByType() {
        getDropdownByType('DataAuthority').then((res: any) => {
          dataMap.dataAuthorityOption = res
        })
      },
      getList() {
        dataMap.listLoading = true
        getRoleList(dataMap.listQuery).then((res: any) => {
          dataMap.list = res.data || []
          dataMap.total = res.total
          dataMap.listLoading = false
        }).catch(() => {
          dataMap.listLoading = false
        })
      },
      handleReset() {
        dataMap.listQuery = {
          name: undefined,
          page: 1,
          size: 20
        }
        dataMap.handleFilter()
      },
      handleFilter() {
        dataMap.getList()
      },
      handleCommand(command: string, val?: any) {
        switch (command) {
          case 'add':
            dataMap.handleCreate()
            break
          case 'edit':
            dataMap.handleUpdate(val)
            break
          case 'delete':
            dataMap.handleDelete(val)
            break
          case 'refresh':
            dataMap.handleFilter()
            break
          case 'current-change':
            dataMap.handleCurrentChange(val)
            break
          case 'size-change':
            dataMap.handleSizeChange(val)
            break
        }
      },
      handleCreate() {
        dataMap.activeId = 0
        dataMap.fromFieldData = cloneDeep(dataMap.initFormData)
        dataMap.dialogFormVisible = true
      },
      handleDelete(row: any) {
        ElMessageBox.confirm(
          '确定要删除选中数据吗？',
          '删除',
          {
            confirmButtonText: t('table.confirm'),
            cancelButtonText: t('table.cancel'),
            type: 'warning',
            cancelButtonClass: 'el-button--danger'
          }
        ).then(() => {
          deleteRolesById(row.id).then(() => {
            dataMap.getList()
            ElMessage({
              message: '删除成功',
              type: 'success',
              duration: 1000
            })
          })
        })
      },
      handleUpdate(row: any) {
        dataMap.activeId = row.id
        dataMap.fromFieldData = cloneDeep(dataMap.initFormData)

        getRoleListByRoleId(row.id).then((res: any) => {
          dataMap.fromFieldData.forEach((k: any) => {
            k.formList.forEach((field: any) => {
              field.value = res[field.fieldKey] || field.value
            })
          })
          dataMap.dialogStatus = 'update'
          dataMap.dialogFormVisible = true
        })
      },
      handleSubmit() {
        dataMap.dialogFormVisible = false
        dataMap.getList()
      },
      handleResolveTempData(tempModel: any) {
        tempModel.accessIds = Array.from(new Set(tempModel.accessIds.flat()))
      },
      getMenuTree(id?: number) {
        getMenuTree({ userId: id }).then((res: any) => {
          this.menuTree = res
        })
      },
      handleCurrentChange(page?: any) {
        dataMap.listQuery.page = page
        dataMap.getList()
      },
      handleSizeChange(size: any) {
        dataMap.listQuery.size = size
        dataMap.handleCurrentChange(1)
      },
      loadTableSelectOption() {
        dataMap.columnAllData.forEach((column: any) => {
          if (column.selectType === 1 && column.url) {
            getDropdownByType(column.url).then((res: any) => {
              column.option = res
                ? res.map((k: any) => {
                  return {
                    ...k,
                    name: k.mark
                  }
                })
                : []
            })
          }
        })
      }
    })
    onMounted(() => {
      dataMap.loadTableSelectOption()
      dataMap.getList()
    })
    return { t, ...toRefs(dataMap), dataForm }
  }
})
</script>
